import React, { useState } from "react";
import JSZip from "jszip";
import { DOMParser } from "xmldom";

// Component to analyze any EPUB structure without assumptions
const EpubStructureAnalyzer = () => {
  const [file, setFile] = useState(null);
  const [structure, setStructure] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const readFileAsArrayBuffer = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsArrayBuffer(file);
    });
  };

  const analyzeEpub = async () => {
    if (!file) return;

    setLoading(true);
    setError(null);
    setStructure(null);

    try {
      // Read the EPUB file
      const content = await readFileAsArrayBuffer(file);

      // Extract using JSZip
      const zip = new JSZip();
      const extracted = await zip.loadAsync(content);

      // Find and analyze all files in the EPUB
      const fileEntries = Object.entries(extracted.files);

      // Categorize files by type/extension
      const fileTypes = {};
      for (const [path, zipObj] of fileEntries) {
        if (!zipObj.dir) {
          const extension = path.split(".").pop().toLowerCase();
          if (!fileTypes[extension]) {
            fileTypes[extension] = [];
          }
          fileTypes[extension].push(path);
        }
      }

      // Find the OPF file (contains metadata and content references)
      let opfFile = null;
      let rootFolder = "";

      // Try to find the container.xml which points to the OPF file
      if (extracted.files["META-INF/container.xml"]) {
        const containerXml =
          await extracted.files["META-INF/container.xml"].async("text");
        const parser = new DOMParser();
        const containerDoc = parser.parseFromString(
          containerXml,
          "application/xml",
        );
        const rootFileElement =
          containerDoc.getElementsByTagName("rootfile")[0];

        if (rootFileElement) {
          opfFile = rootFileElement.getAttribute("full-path");
          const lastSlash = opfFile.lastIndexOf("/");
          if (lastSlash !== -1) {
            rootFolder = opfFile.substring(0, lastSlash + 1);
          }
        }
      }

      if (!opfFile) {
        // Fallback: look for any .opf file
        const opfFiles = fileTypes.opf || [];
        if (opfFiles.length > 0) {
          opfFile = opfFiles[0];
          const lastSlash = opfFile.lastIndexOf("/");
          if (lastSlash !== -1) {
            rootFolder = opfFile.substring(0, lastSlash + 1);
          }
        }
      }

      if (!opfFile) {
        throw new Error("Could not find the OPF file in the EPUB");
      }

      // Parse the OPF file
      const opfContent = await extracted.files[opfFile].async("text");
      const parser = new DOMParser();
      const opfDoc = parser.parseFromString(opfContent, "application/xml");

      // Extract metadata
      const metadataEl = opfDoc.getElementsByTagName("metadata")[0];
      const metadataTags = {};

      if (metadataEl) {
        for (let i = 0; i < metadataEl.childNodes.length; i++) {
          const node = metadataEl.childNodes[i];
          if (node.nodeType === 1) {
            // ELEMENT_NODE
            const tagName = node.nodeName;
            const content = node.textContent.trim();

            if (content) {
              // Group by tag name, handling multiple instances
              if (!metadataTags[tagName]) {
                metadataTags[tagName] = [];
              }
              metadataTags[tagName].push(content);
            }
          }
        }
      }

      // Get the manifest items (maps ids to file paths)
      const manifestItems = {};
      const manifestEl = opfDoc.getElementsByTagName("manifest")[0];

      if (manifestEl) {
        const items = manifestEl.getElementsByTagName("item");
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          manifestItems[item.getAttribute("id")] = {
            href: item.getAttribute("href"),
            mediaType: item.getAttribute("media-type"),
          };
        }
      }

      // Get the spine items (reading order)
      const spine = [];
      const spineEl = opfDoc.getElementsByTagName("spine")[0];

      if (spineEl) {
        const itemrefs = spineEl.getElementsByTagName("itemref");
        for (let i = 0; i < itemrefs.length; i++) {
          const idref = itemrefs[i].getAttribute("idref");
          if (manifestItems[idref]) {
            spine.push({
              id: idref,
              href: manifestItems[idref].href,
              mediaType: manifestItems[idref].mediaType,
            });
          }
        }
      }

      // Analyze a sample of HTML content files to determine structure
      const contentSample = [];
      const allHeaders = {};
      const contentPatterns = {};

      // Get a sample of content files from the spine
      const htmlFiles = spine.filter(
        (item) =>
          item.mediaType === "application/xhtml+xml" ||
          item.mediaType === "text/html",
      );

      // Sample size - analyze at most 20 files or all if fewer
      const sampleSize = Math.min(20, htmlFiles.length);

      for (let i = 0; i < sampleSize; i++) {
        const item = htmlFiles[i];
        const filePath = rootFolder + item.href;

        if (extracted.files[filePath]) {
          const htmlContent = await extracted.files[filePath].async("text");
          const htmlDoc = parser.parseFromString(htmlContent, "text/html");

          // Extract headers to analyze content structure
          const fileHeaders = [];

          for (let level = 1; level <= 6; level++) {
            const headers = htmlDoc.getElementsByTagName(`h${level}`);
            for (let j = 0; j < headers.length; j++) {
              const headerText = headers[j].textContent.trim();
              fileHeaders.push({
                level,
                text: headerText,
              });

              // Count occurrences of each header pattern
              if (!allHeaders[headerText]) {
                allHeaders[headerText] = 0;
              }
              allHeaders[headerText]++;

              // Look for patterns in headers (numbers, common prefixes, etc.)
              const numberMatch = headerText.match(/\d+/);
              if (numberMatch) {
                const number = numberMatch[0];
                const pattern = headerText.replace(number, "X");

                if (!contentPatterns[pattern]) {
                  contentPatterns[pattern] = [];
                }

                if (!contentPatterns[pattern].includes(number)) {
                  contentPatterns[pattern].push(number);
                }
              }
            }
          }

          // Extract first paragraph for context
          const paragraphs = htmlDoc.getElementsByTagName("p");
          let firstParagraph = "";

          if (paragraphs.length > 0) {
            firstParagraph = paragraphs[0].textContent.trim();
            if (firstParagraph.length > 200) {
              firstParagraph = firstParagraph.substring(0, 200) + "...";
            }
          }

          contentSample.push({
            path: filePath,
            headers: fileHeaders,
            firstParagraph,
          });
        }
      }

      // Identify potential structure patterns
      const structurePatterns = Object.entries(contentPatterns)
        .filter(([pattern, numbers]) => numbers.length > 1)
        .sort((a, b) => b[1].length - a[1].length)
        .map(([pattern, numbers]) => ({
          pattern,
          count: numbers.length,
          examples: numbers.slice(0, 5),
          likely:
            pattern.includes("Chapter") ||
            pattern.includes("Book") ||
            /^\s*[A-Za-z]+\s+X\s*$/.test(pattern),
        }));

      // Get TOC (Table of Contents) if available
      let tocItems = [];
      const navItem = Object.values(manifestItems).find(
        (item) =>
          item.href.includes("toc") ||
          item.href.includes("nav") ||
          item.mediaType === "application/x-dtbncx+xml",
      );

      if (navItem) {
        const tocPath = rootFolder + navItem.href;
        if (extracted.files[tocPath]) {
          const tocContent = await extracted.files[tocPath].async("text");
          const isNCX = navItem.mediaType === "application/x-dtbncx+xml";

          if (isNCX) {
            // Parse NCX format
            const tocDoc = parser.parseFromString(
              tocContent,
              "application/xml",
            );
            const navPoints = tocDoc.getElementsByTagName("navPoint");

            for (let i = 0; i < navPoints.length; i++) {
              const navPoint = navPoints[i];
              const textNode = navPoint.getElementsByTagName("text")[0];
              const contentNode = navPoint.getElementsByTagName("content")[0];

              if (textNode && contentNode) {
                tocItems.push({
                  text: textNode.textContent.trim(),
                  href: contentNode.getAttribute("src"),
                  playOrder: navPoint.getAttribute("playOrder"),
                });
              }
            }
          } else {
            // Parse HTML format
            const tocDoc = parser.parseFromString(tocContent, "text/html");

            // Look for different TOC patterns
            const navElements = tocDoc.getElementsByTagName("nav");
            let listItems = [];

            // Try to find nav with epub:type="toc" first
            for (let i = 0; i < navElements.length; i++) {
              if (navElements[i].getAttribute("epub:type") === "toc") {
                const items = navElements[i].getElementsByTagName("li");
                if (items.length > 0) {
                  listItems = items;
                  break;
                }
              }
            }

            // If no TOC nav found, try other common patterns
            if (listItems.length === 0) {
              const olElements = tocDoc.getElementsByTagName("ol");
              if (olElements.length > 0) {
                listItems = olElements[0].getElementsByTagName("li");
              } else {
                const ulElements = tocDoc.getElementsByTagName("ul");
                if (ulElements.length > 0) {
                  listItems = ulElements[0].getElementsByTagName("li");
                }
              }
            }

            // Extract TOC entries
            for (let i = 0; i < listItems.length; i++) {
              const a = listItems[i].getElementsByTagName("a")[0];
              if (a) {
                tocItems.push({
                  text: a.textContent.trim(),
                  href: a.getAttribute("href"),
                });
              }
            }
          }
        }
      }

      // Analyze structure for metadata possibilities
      const metadataSuggestions = [];

      // Check if content appears to have chapters
      if (
        structurePatterns.some(
          (p) => p.pattern.includes("Chapter") || p.pattern.includes("chapter"),
        )
      ) {
        metadataSuggestions.push("chapter");
      }

      // Check if content appears to have books/sections
      if (
        structurePatterns.some(
          (p) =>
            p.pattern.includes("Book") ||
            /^\s*[A-Za-z]+\s+X\s*$/.test(p.pattern),
        )
      ) {
        metadataSuggestions.push("book/section");
      }

      // Check if content has numbered verses
      const versePattern = contentSample.some(
        (sample) =>
          sample.firstParagraph &&
          sample.firstParagraph.match(/\d+\s+[A-Za-z]/),
      );

      if (versePattern) {
        metadataSuggestions.push("verse");
      }

      // Prepare the structure output
      setStructure({
        fileName: file.name,
        metadata: metadataTags,
        fileTypes,
        contentFilesCount: htmlFiles.length,
        contentSample: contentSample,
        structurePatterns,
        tocItemsCount: tocItems.length,
        tocItems: tocItems.slice(0, 500),
        metadataSuggestions,
        possibleMetadataFields: [
          ...Object.keys(metadataTags),
          ...metadataSuggestions,
        ],
      });
    } catch (err) {
      console.error("Analysis error:", err);
      setError(err.message || "Failed to analyze EPUB file");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="container"
      style={{ padding: "20px", maxWidth: "800px", margin: "0 auto" }}
    >
      <h2>EPUB Structure Analyzer</h2>

      <div style={{ marginBottom: "20px" }}>
        <input
          type="file"
          accept=".epub"
          onChange={handleFileChange}
          style={{ marginBottom: "10px", display: "block" }}
        />

        <button
          onClick={analyzeEpub}
          disabled={!file || loading}
          style={{
            padding: "8px 16px",
            backgroundColor: "#4285f4",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: file && !loading ? "pointer" : "default",
            opacity: file && !loading ? 1 : 0.6,
          }}
        >
          {loading ? "Analyzing..." : "Analyze EPUB Structure"}
        </button>
      </div>

      {error && (
        <div style={{ color: "red", marginBottom: "20px" }}>Error: {error}</div>
      )}

      {structure && (
        <div
          style={{
            backgroundColor: "#f5f5f5",
            padding: "15px",
            borderRadius: "4px",
          }}
        >
          <h3>Analysis Results for {structure.fileName}</h3>

          <div style={{ marginBottom: "15px" }}>
            <h4>Content Structure Overview</h4>
            <p>
              <strong>Total Content Files:</strong>{" "}
              {structure.contentFilesCount}
            </p>
            <p>
              <strong>Table of Contents Entries:</strong>{" "}
              {structure.tocItemsCount}
            </p>
          </div>

          <div style={{ marginBottom: "15px" }}>
            <h4>Detected Metadata Fields for Pinecone</h4>
            <ul>
              {structure.possibleMetadataFields.map((field, i) => (
                <li key={i}>{field}</li>
              ))}
            </ul>
          </div>

          {structure.structurePatterns.length > 0 && (
            <div style={{ marginBottom: "15px" }}>
              <h4>Content Patterns</h4>
              <ul>
                {structure.structurePatterns.slice(0, 5).map((pattern, i) => (
                  <li key={i}>
                    <strong>Pattern:</strong> "{pattern.pattern}"
                    <span> (found {pattern.count} times, e.g., </span>
                    {pattern.examples.join(", ")})
                  </li>
                ))}
              </ul>
            </div>
          )}

          {structure.tocItems.length > 0 && (
            <div style={{ marginBottom: "15px" }}>
              <h4>Table of Contents (First 50 entries)</h4>
              <ul style={{ maxHeight: "300px", overflowY: "auto" }}>
                {structure.tocItems.map((item, i) => (
                  <li key={i}>{item.text}</li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <h4>Content Sample (First few files)</h4>
            <div style={{ maxHeight: "300px", overflowY: "auto" }}>
              {structure.contentSample.map((sample, i) => (
                <div
                  key={i}
                  style={{
                    marginBottom: "10px",
                    padding: "10px",
                    border: "1px solid #ddd",
                  }}
                >
                  <p>
                    <strong>File:</strong> {sample.path.split("/").pop()}
                  </p>
                  {sample.headers.length > 0 && (
                    <div>
                      <p>
                        <strong>Headers:</strong>
                      </p>
                      <ul>
                        {sample.headers.map((header, j) => (
                          <li key={j}>
                            H{header.level}: {header.text}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {sample.firstParagraph && (
                    <div>
                      <p>
                        <strong>First paragraph:</strong>{" "}
                        {sample.firstParagraph}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EpubStructureAnalyzer;
